import * as React from "react";
import { useTranslation, Trans } from "react-i18next";
import { WorkflowNode } from "../../types";
import { FormGroup } from "../RightAsideHelpers";
import Input from "@/components/Input";
import IconPlus from "@/icons/IconPlus";
import IconClose from "@/icons/IconClose";

interface MultiSelectEditorProps {
  node: WorkflowNode;
  onUpdate: (updated: Partial<WorkflowNode>) => void;
}

export default function MultiSelectEditor({
  node,
  onUpdate
}: MultiSelectEditorProps) {
  const { t } = useTranslation();
  const optionsSource = node.optionsSource ?? "static";
  const options = Array.isArray(node.options) ? node.options : [];

  function addOption() {
    onUpdate({
      options: [...options, { label: "", value: "" }]
    });
  }

  function removeOption(index: number) {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    onUpdate({ options: newOptions });
  }

  return (
    <>
      <FormGroup label={t("multiSelectEditor.optionsSource")}>
        <div className="flex gap-2">
          <button
            type="button"
            aria-pressed={optionsSource === "static"}
            onClick={() => onUpdate({ optionsSource: "static" })}
            className={`flex-1 h-10 rounded-[8px] border-[1.5px] text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
              optionsSource === "static"
                ? "bg-primary border-primary text-white"
                : "bg-white border-gray-300 text-gray-700 hover:border-primary-hover"
            }`}
          >
            {t("multiSelectEditor.staticOptions")}
          </button>
          <button
            type="button"
            aria-pressed={optionsSource === "dynamic"}
            onClick={() => onUpdate({ optionsSource: "dynamic" })}
            className={`flex-1 h-10 rounded-[8px] border-[1.5px] text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
              optionsSource === "dynamic"
                ? "bg-primary border-primary text-white"
                : "bg-white border-gray-300 text-gray-700 hover:border-primary-hover"
            }`}
          >
            {t("multiSelectEditor.dynamicOptions")}
          </button>
        </div>
      </FormGroup>

      {optionsSource === "static" && (
        <FormGroup label={t("multiSelectEditor.options")}>
          <div className="flex flex-col gap-2">
            {options.map((option, i) => (
              <div key={i} className="flex gap-2 items-center">
                <div className="flex-1">
                  <Input
                    placeholder={t("multiSelectEditor.labelPlaceholder")}
                    suffixIcon={
                      <button
                        type="button"
                        aria-label={t("multiSelectEditor.removeOption", { number: i + 1 })}
                        onClick={() => removeOption(i)}
                        className="flex items-center justify-center text-gray-400 hover:text-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500 rounded"
                      >
                        <IconClose />
                      </button>
                    }
                    value={option.label ?? ""}
                    onChange={(label) => {
                      const newOptions = [...options];
                      newOptions[i] = { ...option, label };
                      onUpdate({ options: newOptions });
                    }}
                    onBlur={(label) => {
                      const trimmed = label.trim();
                      if (trimmed !== label) {
                        const newOptions = [...options];
                        newOptions[i] = { ...option, label: trimmed };
                        onUpdate({ options: newOptions });
                      }
                    }}
                  />
                </div>
                <div className="flex-1">
                  <Input
                    placeholder={t("multiSelectEditor.valuePlaceholder")}
                    value={option.value ?? ""}
                    onChange={(value) => {
                      const newOptions = [...options];
                      newOptions[i] = { ...option, value };
                      onUpdate({ options: newOptions });
                    }}
                    onBlur={(value) => {
                      const trimmed = value.trim();
                      if (trimmed !== value) {
                        const newOptions = [...options];
                        newOptions[i] = { ...option, value: trimmed };
                        onUpdate({ options: newOptions });
                      }
                    }}
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addOption}
              className="w-full h-10 px-6 py-2.5 bg-white rounded-[8px] border-[1.5px] border-blue-500 justify-center items-center gap-2 inline-flex hover:bg-blue-50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
            >
              <span className="text-center text-blue-500 text-sm flex items-center font-bold gap-2">
                <IconPlus className="text-blue-500" aria-hidden="true" />
                {t("multiSelectEditor.addOption")}
              </span>
            </button>
          </div>
        </FormGroup>
      )}

      {optionsSource === "dynamic" && (
        <FormGroup label={t("multiSelectEditor.optionsVariable")}>
          <Input
            placeholder={t("multiSelectEditor.optionsVariablePlaceholder")}
            value={node.dynamicOptionsVariable ?? ""}
            onChange={(dynamicOptionsVariable) =>
              onUpdate({ dynamicOptionsVariable })
            }
          />
          <p className="text-xs text-gray-500 mt-1">
            <Trans
              i18nKey="multiSelectEditor.optionsVariableHint"
              components={{ code: <code /> }}
            />
          </p>
        </FormGroup>
      )}

      <FormGroup label={t("multiSelectEditor.selectionLimits")}>
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-xs text-gray-600 mb-1">
              {t("multiSelectEditor.minSelections")}
            </label>
            <Input
              type="number"
              placeholder={t("multiSelectEditor.minSelectionsPlaceholder")}
              value={node.minSelections != null ? String(node.minSelections) : ""}
              onChange={(v) =>
                onUpdate({ minSelections: v === "" ? undefined : Number(v) })
              }
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-600 mb-1">
              {t("multiSelectEditor.maxSelections")}
            </label>
            <Input
              type="number"
              placeholder={t("multiSelectEditor.maxSelectionsPlaceholder")}
              value={node.maxSelections != null ? String(node.maxSelections) : ""}
              onChange={(v) =>
                onUpdate({ maxSelections: v === "" ? undefined : Number(v) })
              }
            />
          </div>
        </div>
      </FormGroup>

      <FormGroup label={t("multiSelectEditor.largeListStrategy")}>
        <select
          value={node.largeListStrategy ?? "both"}
          onChange={(e) => onUpdate({ largeListStrategy: e.target.value as "paginate" | "cross-channel" | "both" })}
          className="w-full h-10 rounded-[8px] border-[1.5px] border-gray-300 px-3 text-sm"
          aria-label={t("multiSelectEditor.largeListStrategy")}
        >
          <option value="both">{t("multiSelectEditor.strategyAuto")}</option>
          <option value="paginate">{t("multiSelectEditor.strategyPaginate")}</option>
          <option value="cross-channel">{t("multiSelectEditor.strategyCrossChannel")}</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">{t("multiSelectEditor.strategyHint")}</p>
      </FormGroup>

      {optionsSource === "static" && options.length > 0 && (
        <FormGroup label={t("multiSelectEditor.preview")}>
          <div
            className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200"
            aria-hidden="true"
          >
            {options.map((option, i) => (
              <div
                key={i}
                className="min-h-[44px] min-w-[44px] px-4 py-2 rounded-lg border-[1.5px] border-primary-hover bg-white text-primary text-sm font-medium flex items-center"
              >
                {option.label || t("multiSelectEditor.emptyOption")}
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {t("multiSelectEditor.previewHint")}
          </p>
        </FormGroup>
      )}

      <div className="mx-0 mb-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-xs text-amber-800">
          <strong>{t("multiSelectEditor.storageNoteTitle")}</strong>{" "}
          <Trans
            i18nKey="multiSelectEditor.storageNoteBody"
            values={{
              variableRef: "<<variableName>>",
              indexedRef: "<<variableName[0]>>"
            }}
            components={{ code: <code />, strong: <strong /> }}
          />
        </p>
      </div>
    </>
  );
}
